import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { RetryLink } from "@apollo/client/link/retry";
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import FrameConfig from "@lib/classes/FrameConfig";

interface CreateClientProps {
  errorHandler?: () => void;
  extractedCache?: NormalizedCacheObject;
  requestId?: string;
  sessionId?: string;
  siteId: string;
  uri: string;
}

const sessionLink = setContext((_, { headers }) => {
  const jsonHeaders = new FrameConfig().jsonHeaders || {};
  return {
    headers: {
      ...headers,
      headers: Object.keys(jsonHeaders)
        .map((key) => `${key}=${jsonHeaders[key]}`)
        .join("&"),
      override: new FrameConfig().override || "",
      language: new FrameConfig().language || "en",
    },
  };
});

function createCache(siteId: string, extractedCache?: NormalizedCacheObject) {
  const cache = new InMemoryCache();

  if (extractedCache) {
    cache.restore(extractedCache);

    if (global.window) {
      // await before instantiating ApolloClient, else queries might run before the cache is persisted
      // persistCacheSync({ cache, storage: new SessionStorageWrapper(window.sessionStorage) });

      // If site ID changed clear cache.
      let cacheSiteId = sessionStorage.getItem("apollo-site");
      if (cacheSiteId !== siteId) cache.reset();
      sessionStorage.setItem("apollo-site", siteId);
    }
  }

  return cache;
}

// Encode the body text. Not to make it secure but to make it just a little harder for someone
// to decode the text and see what kind of API we're using.
function encodeTextBody(text?: string) {
  if (!text) return text;
  let result;

  result = btoa(encodeURIComponent(text)); // base64 text
  result = btoa(result.split("").reverse().join("")); // reverse the base64 string and base64 it
  result = btoa(result.split("").reverse().join("")); // reverse the base64 string and base64 it
  return `{"q": "${result}"}`;
}

async function fetchImplementation(uri: string, options: RequestInit = {}) {
  const isEncoding = shouldEncode(uri, options);

  options.body = isEncoding ? encodeTextBody(options.body) : options.body;

  if (options.headers.cookie === undefined) delete options.headers.cookie;

  const response = await fetch(uri, options);
  const newResponse = response.clone();

  let json = await response.json();
  json = atob(json.a.replace(/\s/g, "")); // Decode it
  json = atob(json.split("").reverse().join("").replace(/\s/g, "")); // Decode it reversed
  json = atob(json.split("").reverse().join("").replace(/\s/g, "")); // Decode it reversed
  json = decodeURIComponent(json);

  newResponse.json = () => JSON.parse(json);
  newResponse.text = async () => json;
  return newResponse;
}

function shouldEncode(url: string, options: RequestInit) {
  if (!options.method || options.method.toLowerCase() !== "post") return false;
  url = url.split("?")[0].split("#")[0];
  if (!url.endsWith("/d")) return false;
  return true;
}

export default function ({
  errorHandler,
  extractedCache,
  requestId,
  sessionId,
  siteId,
  uri,
}: CreateClientProps) {
  return new ApolloClient({
    link: ApolloLink.from([
      onError(({ operation, response, graphQLErrors, networkError }) => {
        if (networkError) {
          console.error("Apollo network error", networkError);
          console.error("operation", operation);
          console.error("response", response);
          if (errorHandler) errorHandler();
        }

        if (graphQLErrors) {
          console.error("Apollo graphQL error", graphQLErrors);
          console.error("operation", operation);
          console.error("response", response);
          if (errorHandler) errorHandler();
        }
      }),
      sessionLink,
      new RetryLink({
        attempts: {
          max: 4,
          retryIf: (error, _operation) => !!error,
        },
      }),
      new BatchHttpLink({
        fetch: fetchImplementation,
        uri: uri,
        credentials: "include",
        headers: {
          APIAppName: process.env.NEXT_PUBLIC_APP_NAME || "",
          APIAppVersion: process.env.NEXT_PUBLIC_APP_VERSION || "",
          APIAppRequestId: requestId,
          APIAppSession: sessionId,
        },
      }),
    ]),
    cache: createCache(siteId, extractedCache),
  });
}
